export interface StepOutputBinding {
  kind: 'StepOutput';
  stepId: string;
  target: string;
}

export interface ProcessingJobInputBinding {
  kind: 'ProcessingJobInput';
  target: string;
}

export interface ConstantBinding {
  kind: 'Constant';
  value: unknown;
}

export interface ExpressionBinding {
  kind: 'Expression';
  expression: string;
}

export type InputBinding =
  | StepOutputBinding
  | ProcessingJobInputBinding
  | ConstantBinding
  | ExpressionBinding;

export interface RunAfterRef {
  stepId: string;
  enableOnCondition?: boolean;
  routeKey?: string;
}

export interface WorkflowStepDto {
  id: string;
  actionId: string;
  input: Record<string, InputBinding>;
  settings: Record<string, unknown>;
  runAfter: RunAfterRef[];
  nestedSteps?: Record<string, WorkflowStepDto>;
}

export interface WorkflowDto {
  name: string;
  steps: Record<string, WorkflowStepDto>;
}

export interface RawInputBinding {
  // Historic engine shape (PascalCase)
  StepId?: string;
  Target?: string;
  TargetType?: string;
  // API shape (camelCase)
  stepId?: string | null;
  target?: string | null;
  targetType?: string | null;
  expression?: string | null;
  [key: string]: unknown;
}

export interface RawRunAfterRef {
  // Historic engine shape
  StepId?: string;
  EnableOnCondition?: boolean;
  RouteKey?: string;
  // API shape
  stepId?: string;
  enableOnCondition?: boolean;
  routeKey?: string;
}

export interface RawWorkflowStep {
  // Historic engine shape
  ActionId?: string;
  Input?: Record<string, RawInputBinding>;
  Settings?: Record<string, unknown>;
  RunAfter?: RawRunAfterRef[];
  Steps?: Record<string, RawWorkflowStep>;

  // API shape (see Swagger `Step` schema)
  actionId?: string;
  input?: Record<string, RawInputBinding> | null;
  settings?: Record<string, unknown> | null;
  runAfter?: RawRunAfterRef[] | null;
  steps?: Record<string, RawWorkflowStep> | null;
}

export interface RawWorkflowDto {
  // Historic engine shape (test resources)
  Name?: string;
  Steps?: Record<string, RawWorkflowStep>;

  // API shape (see Swagger `Workflow` schema)
  workflowId?: string;
  basedOnWorkflowId?: string | null;
  name?: string | null;
  steps?: Record<string, RawWorkflowStep> | null;
  // Additional fields like `settings`, `isStandardWorkflow` are ignored for now.
}

const normalizeInputBinding = (binding: unknown): InputBinding => {
  if (!binding || typeof binding !== 'object') {
    return { kind: 'Constant', value: binding };
  }

  const raw = binding as RawInputBinding;

  const targetType = raw.TargetType ?? raw.targetType;
  const target = raw.Target ?? raw.target;
  const stepId = raw.StepId ?? raw.stepId;

  if (targetType === 'ProcessingJobInput' && typeof target === 'string') {
    return {
      kind: 'ProcessingJobInput',
      target,
    };
  }

  if (typeof stepId === 'string' && typeof target === 'string') {
    return {
      kind: 'StepOutput',
      stepId,
      target,
    };
  }

  if (typeof raw.expression === 'string') {
    return { kind: 'Expression', expression: raw.expression };
  }

  return { kind: 'Constant', value: binding };
};

const normalizeRunAfter = (items: RawRunAfterRef[] | undefined | null): RunAfterRef[] => {
  if (!items || items.length === 0) {
    return [];
  }

  return items.map((item) => {
    const stepId = item.StepId ?? item.stepId;

    return {
      stepId: stepId ?? '',
      enableOnCondition: item.EnableOnCondition ?? item.enableOnCondition,
      routeKey: item.RouteKey ?? item.routeKey,
    };
  });
};

const normalizeWorkflowStep = (id: string, raw: RawWorkflowStep): WorkflowStepDto => {
  const input: Record<string, InputBinding> = {};

  const rawInput = raw.Input ?? raw.input ?? {};
  if (rawInput && typeof rawInput === 'object') {
    Object.entries(rawInput).forEach(([key, value]) => {
      input[key] = normalizeInputBinding(value);
    });
  }

  const actionId = raw.ActionId ?? raw.actionId ?? '';
  const settings = raw.Settings ?? raw.settings ?? {};
  const runAfter = normalizeRunAfter(raw.RunAfter ?? raw.runAfter ?? null);

  const step: WorkflowStepDto = {
    id,
    actionId,
    input,
    settings,
    runAfter,
  };

  const rawSteps = raw.Steps ?? raw.steps ?? {};
  if (rawSteps && typeof rawSteps === 'object' && Object.keys(rawSteps).length > 0) {
    const nestedSteps: Record<string, WorkflowStepDto> = {};
    Object.entries(rawSteps).forEach(([nestedId, nestedRaw]) => {
      nestedSteps[nestedId] = normalizeWorkflowStep(nestedId, nestedRaw);
    });
    step.nestedSteps = nestedSteps;
  }

  return step;
};

export const normalizeWorkflow = (raw: RawWorkflowDto): WorkflowDto => {
  const steps: Record<string, WorkflowStepDto> = {};

  const rawSteps = raw.Steps ?? raw.steps ?? {};
  if (rawSteps && typeof rawSteps === 'object') {
    Object.entries(rawSteps).forEach(([id, rawStep]) => {
      steps[id] = normalizeWorkflowStep(id, rawStep);
    });
  }

  const name = raw.Name ?? raw.name ?? '';

  return {
    name,
    steps,
  };
};

const denormalizeInputBinding = (binding: InputBinding): RawInputBinding => {
  switch (binding.kind) {
    case 'StepOutput':
      return {
        stepId: binding.stepId,
        target: binding.target,
      };
    case 'ProcessingJobInput':
      return {
        target: binding.target,
        targetType: 'ProcessingJobInput',
      };
    case 'Expression':
      return { expression: binding.expression };
    case 'Constant':
    default:
      // Historic engine shape allowed raw constants in the Input map.
      // We preserve that here by returning the raw value, even though
      // the type is declared as RawInputBinding.
      return binding.value as unknown as RawInputBinding;
  }
};

const denormalizeRunAfter = (items: RunAfterRef[]): RawRunAfterRef[] =>
  items.map((item) => ({
    stepId: item.stepId,
    ...(item.enableOnCondition !== undefined && {
      enableOnCondition: item.enableOnCondition,
    }),
    ...(item.routeKey !== undefined &&
      item.routeKey !== null &&
      item.routeKey !== '' && {
        routeKey: item.routeKey,
      }),
  }));

const denormalizeWorkflowStep = (step: WorkflowStepDto): RawWorkflowStep => {
  const input: Record<string, RawInputBinding> = {};
  Object.entries(step.input).forEach(([key, value]) => {
    input[key] = denormalizeInputBinding(value);
  });

  const raw: RawWorkflowStep = {
    actionId: step.actionId,
    input,
    settings: step.settings,
    runAfter: denormalizeRunAfter(step.runAfter),
  };

  if (step.nestedSteps) {
    const nested: Record<string, RawWorkflowStep> = {};
    Object.entries(step.nestedSteps).forEach(([id, nestedStep]) => {
      nested[id] = denormalizeWorkflowStep(nestedStep);
    });
    raw.steps = nested;
  }

  return raw;
};

export const denormalizeWorkflow = (workflow: WorkflowDto): RawWorkflowDto => {
  const steps: Record<string, RawWorkflowStep> = {};

  Object.entries(workflow.steps).forEach(([id, step]) => {
    steps[id] = denormalizeWorkflowStep(step);
  });

  return {
    name: workflow.name,
    steps,
  };
};
